import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { PerformanceChartData } from 'app/modules/analytics/modules/performance/types/chartTypes';
import AnalyticsExportChart from './AnalyticsExportChart';
import AnalyticsExportPageFrame, {
  type AnalyticsExportPageProfile,
} from './AnalyticsExportPageFrame';
import { analyticsExportTheme as theme } from './analyticsExportTheme';

interface Props {
  title: string;
  subtitle: string;
  chartType: 'line' | 'bar';
  stacked?: boolean;
  data: PerformanceChartData;
  /** Headline figure shown beside the chart, mirroring the Figma stat card. */
  headlineLabel: string;
  headlineValue: string;
  profile: AnalyticsExportPageProfile;
  pageNumber: number;
  totalPages: number;
}

const AnalyticsExportChartPage: FC<Props> = ({
  title,
  subtitle,
  chartType,
  stacked,
  data,
  headlineLabel,
  headlineValue,
  profile,
  pageNumber,
  totalPages,
}) => {
  const { t } = useTranslation();
  const isEmpty = data.labels.length === 0;

  return (
    <AnalyticsExportPageFrame
      title={title}
      subtitle={subtitle}
      profile={profile}
      pageNumber={pageNumber}
      totalPages={totalPages}
      dataCy="analytics_export-public_chart-page"
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-items-stretch tw-gap-4 tw-py-8">
        <div
          className={`${theme.tile} tw-flex tw-w-[64mm] tw-shrink-0 tw-flex-col tw-gap-2 tw-self-start tw-rounded-2xl tw-px-6 tw-py-4`}
        >
          <span className={`tw-text-lg ${theme.tileLabel}`}>
            {headlineLabel}
          </span>
          <span
            className={`tw-text-3xl tw-font-bold tw-leading-none ${theme.tileValue}`}
          >
            {headlineValue}
          </span>
          {data.datasets.length > 1 ? (
            <ul className="tw-mt-2 tw-flex tw-flex-col tw-gap-1">
              {data.datasets.map((dataset) => (
                <li
                  key={dataset.label}
                  className={`tw-flex tw-items-center tw-gap-2 tw-text-sm ${theme.subtitle}`}
                >
                  <span
                    className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full"
                    style={{ backgroundColor: dataset.color }}
                  />
                  {dataset.label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div
          className={`${theme.chartSurface} analytics-export-chart tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-px-6 tw-py-4`}
        >
          {isEmpty ? (
            <p
              className={`tw-flex tw-flex-1 tw-items-center tw-justify-center tw-text-base ${theme.subtitle}`}
            >
              {t('analyticsPerformance.noDataAvailable')}
            </p>
          ) : (
            <AnalyticsExportChart
              chartType={chartType}
              stacked={stacked}
              data={data}
            />
          )}
        </div>
      </div>
    </AnalyticsExportPageFrame>
  );
};

export default AnalyticsExportChartPage;
